<script setup lang="ts">
import { RouterLink, useRouter, useRoute } from 'vue-router'
import { Icon } from '@iconify/vue'
import Button from 'primevue/button'
import {getBatchById, returnFromSupplier} from '@/api/batches'
import { useReturnFromSupplierForm } from '@/composables/useReturnFromSupplier'
import EditReturnBatchFromSupplier from '@/components/features/batches/forms/EditReturnBatchFromSupplier.vue'
import {onMounted, ref, watch} from 'vue'
import type { ErrorResponse } from '@/types/error-types'
import {getProductById} from "@/api/products";
import type {BatchRoll, ReturnBatchRoll} from "@/types/batch-types";

const { values, handleSubmit, showToast, setFieldValue, loading, errors } =
  useReturnFromSupplierForm()
const router = useRouter()
const route = useRoute()
const batchId = route.params.id as string
const originalBatch = ref<any>(null)
const isUpdatingFromChild = ref(false)

const submit = async () => {
  loading.value = true

  if(!values.dateReturned){
    showToast('error', 'Error', 'Please select a date returned.')
    loading.value = false
    return
  }

  const validRolls = values.rolls.filter((roll: BatchRoll) => {
    if (!roll.id) return true

    if (!originalBatch.value?.rolls) return false

    const rollsSentToSupplier = originalBatch.value.rolls.filter((batchRoll: BatchRoll) =>
      batchRoll.isActive && batchRoll.rollState === 1
    )

    return rollsSentToSupplier.some((sentRoll: BatchRoll) => sentRoll.id === roll.id)
  })

  const returnedBatch = {
    previousBatchId: values.id,
    finishingSupplierId: values.finishingSupplierId,
    batchNumber: values.batchNumber,
    totalWeight: values.weight,
    totalLengthValue: values.lengthValue,
    totalLengthUnit: values.totalLengthUnit,
    totalPercentageWeightLostOrGained: values.totalPercentageWeightLostOrGained,
    dateReturned: values.dateReturned,
    notes: values.notes,
    lastEditedBy: values.lastEditedBy,
    returnedRolls: validRolls.map((roll: ReturnBatchRoll) => ({
      previousRollId: roll.id,
      rollNumber: roll.rollNumber,
      newLengthValue: roll.lengthValue,
      newLengthUnit: roll.lengthUnit,
      newWeight: roll.weight,
    }))
  }

  await returnFromSupplier(returnedBatch, batchId)
    .then(() => {
      loading.value = false
      showToast(
        'success',
        'Batch Returned from Supplier',
        'You have successfully returned this batch from supplier'
      )
      router.push('/batches')
    })
    .catch((error: { response?: { data: ErrorResponse } }) => {
      loading.value = false
      const errorMessages =
        error.response?.data?.errors?.map((err) => err.description).join('\n') ||
        'Unable to return batch from supplier.'
      showToast('error', 'An error occurred!', errorMessages)
    })
}

const fabricYield = ref({
  value: 0,
  unit: 0
});

watch(fabricYield, (newYield) => {
  if (isUpdatingFromChild.value) return;
  if (newYield.value && values.rolls && values.rolls.length > 0) {
    const rollsSentToSupplier = originalBatch.value?.rolls?.filter((roll: BatchRoll) =>
        roll.isActive && roll.rollState === 1
    ) || []

    const validRolls = values.rolls.filter((roll: BatchRoll) => {
      if (!roll.id) return true
      return rollsSentToSupplier.some((sentRoll: BatchRoll) => sentRoll.id === roll.id)
    })

    const updatedRolls = validRolls.map((roll: BatchRoll) => {
      if (roll.weight) {
        return {
          ...roll,
          lengthValue: roll.weight * newYield.value,
          lengthUnit: newYield.unit
        };
      }
      return roll;
    });

    setFieldValue('rolls', updatedRolls);

    const totalLength = updatedRolls.reduce((sum: number, roll: BatchRoll) =>
        sum + (roll.lengthValue || 0), 0);
    setFieldValue('lengthValue', totalLength);
    setFieldValue('lengthUnit', newYield.unit);
  }
}, { deep: true });

const handleChildUpdate = (updatedBatch: any) => {
  isUpdatingFromChild.value = true;

  const validRolls = updatedBatch.rolls?.filter((roll: BatchRoll) => {
    if (!roll.id) return true

    if (!originalBatch.value?.rolls) return false

    const rollsSentToSupplier = originalBatch.value.rolls.filter((batchRoll: BatchRoll) =>
        batchRoll.isActive && batchRoll.rollState === 1
    )

    return rollsSentToSupplier.some((sentRoll: BatchRoll) => sentRoll.id === roll.id)
  }) || []

  setFieldValue('rolls', validRolls)
  setFieldValue('lengthValue', updatedBatch.lengthValue)
  setFieldValue('lengthUnit', updatedBatch.lengthUnit)
  setFieldValue('weight', updatedBatch.weight)
  setFieldValue('batchNumber', updatedBatch.batchNumber)
  setFieldValue('totalPercentageWeightLostOrGained', updatedBatch.totalPercentageWeightLostOrGained);

  setTimeout(() => {
    isUpdatingFromChild.value = false;
  }, 100);
}

onMounted(async () => {
  try {
    const batch = await getBatchById(batchId);
    originalBatch.value = batch;

    if (batch && batch.finishingQualityId) {
      const product = await getProductById(batch.finishingQualityId);

      if (product) {
        fabricYield.value = {
          value: product.fabricYieldValue || 0,
          unit: product.fabricYieldUnit || 0
        };

        if (values.rolls && values.rolls.length > 0) {
          const rollsSentToSupplier = batch.rolls?.filter((roll: BatchRoll) =>
              roll.isActive && roll.rollState === 1
          ) || []

          const validInitialRolls = values.rolls.filter((roll: BatchRoll) => {
            if (!roll.id) return true

            return rollsSentToSupplier.some((sentRoll: BatchRoll) => sentRoll.id === roll.id)
          })

          const updatedRolls = validInitialRolls.map((roll: BatchRoll) => {
            if (roll.weight && fabricYield.value.value) {
              return {
                ...roll,
                lengthValue: roll.weight * fabricYield.value.value,
                lengthUnit: fabricYield.value.unit
              };
            }
            return roll;
          });

          setFieldValue('rolls', updatedRolls);

          const totalLength = updatedRolls.reduce((sum: number, roll: BatchRoll) => sum + (roll.lengthValue || 0), 0);
          setFieldValue('lengthValue', totalLength);
          setFieldValue('lengthUnit', fabricYield.value.unit);
        }
      }
    }
  } catch (error) {
    console.error("Error fetching batch or product details:", error);
    showToast('error', 'An error occurred!', 'Failed to load batch or product details');
  }
});
</script>

<template>
  <div class="p-10 bg-white rounded-xl">
    <div v-if="loading" class="flex justify-center items-center h-64">
      <span>Loading...</span>
    </div>
    <div v-else>
      <div class="flex justify-between items-center mb-10">
        <RouterLink
          to="/batches"
          class="flex items-center gap-x-2 group hover:text-primary-default"
        >
          <Icon
            icon="material-symbols:arrow-back-rounded"
            class="text-3xl group-hover:translate-x-2 transition-transform"
          />
          <span>Back</span>
        </RouterLink>

        <h1 class="font-semibold text-2xl">Finished</h1>

        <Button label="Save Batch" @click="submit">
          <template #icon>
            <Icon icon="material-symbols:save" class="text-xl ml-3 order-1" />
          </template>
        </Button>
      </div>
      <EditReturnBatchFromSupplier
          :modelValue="values"
          @update:modelValue="handleChildUpdate"
          :disabled="false"
          type="add"
          :fabricYieldValue="fabricYield.value"
          :fabricYieldUnit="fabricYield.unit"
          :batchId="batchId"
      />
    </div>
  </div>
</template>
